import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, ClipboardText } from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function Approvals() {
  const [leaves, setLeaves] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaves", { params: { status: tab === "all" ? undefined : tab } });
      setLeaves(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const catOf = (t) => ((t === "Vacation" || t === "Annual") ? "VL" : t === "Sick" ? "SL" : "Others");
  const summary = leaves.reduce((acc, r) => {
    const c = catOf(r.leave_type);
    acc[c].count += 1;
    acc[c].days += Number(r.days || 0);
    return acc;
  }, { VL: { count: 0, days: 0 }, SL: { count: 0, days: 0 }, Others: { count: 0, days: 0 } });
  const CATS = [
    { key: "VL", label: "Vacation Leave (VL)", cls: "text-primary" },
    { key: "SL", label: "Sick Leave (SL)", cls: "text-warning" },
    { key: "Others", label: "Others", cls: "text-muted-foreground" },
  ];

  const review = async (id, status) => {
    try {
      await api.put(`/leaves/${id}/review`, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Management</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Leave Approvals</h1>
        <p className="text-muted-foreground mt-2">Review and action time-off requests from staff.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="leave-summary">
        {CATS.map((c) => (
          <Card key={c.key} className="p-4 border-border shadow-none" data-testid={`leave-summary-${c.key}`}>
            <p className="text-sm text-muted-foreground">{c.label}</p>
            <p className={`mt-1 text-2xl font-heading font-extrabold ${c.cls}`}>{summary[c.key].count}</p>
            <p className="text-xs text-muted-foreground">{summary[c.key].days} day{summary[c.key].days === 1 ? "" : "s"} total</p>
          </Card>
        ))}
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : leaves.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <ClipboardText className="h-6 w-6 mx-auto mb-2 opacity-40" />No {tab === "all" ? "" : tab} requests.
                </TableCell></TableRow>
              ) : leaves.map((r) => (
                <TableRow key={r.id} data-testid={`approval-row-${r.id}`} className="animate-fade-up">
                  <TableCell>
                    <p className="font-medium">{r.user_name}</p>
                    <p className="text-xs text-muted-foreground">{r.department || r.user_email}</p>
                  </TableCell>
                  <TableCell>{r.leave_type}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.start_date} → {r.end_date}</TableCell>
                  <TableCell>{r.days}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-muted-foreground text-sm" title={r.reason}>{r.reason}</TableCell>
                  <TableCell><Badge variant="outline" className={`capitalize ${STATUS_BADGE[r.status]}`}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {r.status === "pending" ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => review(r.id, "approved")}
                          className="h-8 bg-success text-white hover:bg-success/90 gap-1"><Check weight="bold" className="h-3.5 w-3.5" /> Approve</Button>
                        <Button size="sm" variant="outline" data-testid={`reject-${r.id}`} onClick={() => review(r.id, "rejected")}
                          className="h-8 text-destructive border-destructive/30 hover:bg-destructive/10 gap-1"><X weight="bold" className="h-3.5 w-3.5" /> Reject</Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">by {r.reviewer_name || "—"}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
